import React, { useCallback, useMemo, useState } from 'react';
import { Box, Chip, TablePagination } from '@mui/material';
import RouteIcon from '@mui/icons-material/Route';
import { useLanguage } from '../../../../context/LanguageContext';
import { Store } from '../../../../types/Store';
import StoreCard from './common/StoreCard';
import StoreTable from './common/StoreTable';

const PAGE_SIZE = 10;

interface ParcoursProps {
    store: Store;
    isEditable?: boolean;
}

const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
};

const isActive = (value: unknown) => value === 1 || value === '1' || value === true;

const Parcours = ({ store }: ParcoursProps) => {
    const { t } = useLanguage();
    const parcours = store.PARCOURS?.LIST || [];
    const [page, setPage] = useState(0);

    const renderStatus = useCallback((value: unknown) => {
        const active = isActive(value);
        return (
            <Chip
                label={active ? t('pages.store.active') : t('pages.store.inactive')}
                size="small"
                color={active ? 'success' : 'error'}
                variant="filled"
            />
        );
    }, [t]);

    const columns = useMemo(() => [
        { label: t('pages.store.id'), key: 'ACTION_ID' },
        { label: t('pages.store.name'), key: 'NAME' },
        { label: t('pages.store.channel'), key: 'CHANNEL_NAME' },
        {
            label: t('pages.store.startDate'),
            render: (p: Record<string, unknown>) => formatDate(p.OCC_START_DATE as string | undefined),
        },
        {
            label: t('pages.store.endDate'),
            render: (p: Record<string, unknown>) => formatDate(p.OCC_END_DATE as string | undefined),
        },
        {
            label: t('pages.store.edition'),
            align: 'center' as const,
            render: (p: Record<string, unknown>) => renderStatus(p.EDIT),
        },
        {
            label: t('pages.store.use'),
            align: 'center' as const,
            render: (p: Record<string, unknown>) => renderStatus(p.USE),
        },
    ], [t, renderStatus]);

    const paginated = parcours.length > PAGE_SIZE
        ? parcours.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
        : parcours;

    return (
        <StoreCard
            title={t('pages.store.customerJourney')}
            icon={<RouteIcon sx={{ color: 'primary.main' }} />}
            isEmpty={parcours.length === 0}
            emptyMessage={t('pages.store.noCustomerJourney')}
        >
            <StoreTable columns={columns} data={paginated} />
            {parcours.length > PAGE_SIZE && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid', borderColor: 'divider' }}>
                    <TablePagination
                        component="div"
                        count={parcours.length}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={PAGE_SIZE}
                        rowsPerPageOptions={[PAGE_SIZE]}
                        labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
                    />
                </Box>
            )}
        </StoreCard>
    );
};

export default Parcours;
